/**
 * Where the reader was before the screen they are on now, as the router
 * last resolved it. Lets a screen say how it was reached (from home, from
 * the favorites drawer, straight from a link) without every link that can
 * lead to it having to announce itself.
 *
 * Fed by the router's `onResolved` (`create-tenant-app.tsx`). It is the
 * origin of the *last navigation*, so read it when the screen opens, not
 * later.
 */
export interface NavigationOrigin {
	pathname: string;
	/** The search params, as the router parsed them. */
	search: Record<string, unknown>;
}

let origin: NavigationOrigin | undefined;

/** `undefined` for the first screen of the visit: nothing came before it. */
export function setNavigationOrigin(next: NavigationOrigin | undefined): void {
	origin = next;
}

export function getNavigationOrigin(): NavigationOrigin | undefined {
	return origin;
}

export type OpenedFrom = 'home' | 'favorite' | 'downloads' | 'deeplink' | 'other';

/**
 * How the current screen was reached. The home screen's drawers keep their
 * open state in `?dialog=`, so a reader who came from one of them left home
 * with it set.
 */
export function openedFrom(from: NavigationOrigin | undefined = origin): OpenedFrom {
	if (!from) {
		return 'deeplink';
	}
	if (from.search.dialog === 'favorites') {
		return 'favorite';
	}
	if (from.search.dialog === 'downloads') {
		return 'downloads';
	}
	return from.pathname === '/' ? 'home' : 'other';
}
